import type { Doc, Id } from "../../_generated/dataModel";
import type { FilterRule, FilterRuleCtx } from "../../lib/types/filters_types";

type TaggableTable = "quotations" | "invoices" | "sales" | "services" | "clients" | "products" | "suppliers";

/** Máximo de etiquetas combinadas en un mismo filtro. */
const MAX_TAGS_IN_FILTER = 20;

/**
 * Regla de filtro por etiqueta para las tablas paginadas. Arg `tag`: uno o
 * varios slugs separados por coma (lo que pone el selector múltiple de la tabla
 * en la URL, `?tag=urgente,vip`). Devuelve los registros con CUALQUIERA de esas
 * etiquetas.
 *
 * Resuelve cada etiqueta por slug en la empresa, toma sus asociaciones por índice
 * (`by_company_and_tag`) y devuelve los documentos etiquetados de ese tipo. Se
 * intersecta con las demás reglas (estado, búsqueda, rangos…). Al ser un arg
 * extra, la paginación por aggregate/índice se desactiva sola y se usa el
 * camino en memoria (ver index_paginate.ts / aggregate_fast_path.ts).
 */
export function createTagFilterRule<T extends TaggableTable>(
  entityType: T,
  /** Igual que el `scope` de la tabla: "branch" acota a la sucursal activa. */
  scope: "branch" | "company" = "branch",
): FilterRule<Doc<T>> {
  return {
    priority: 2,
    build: async (ctx: FilterRuleCtx, args) => {
      const { tag, includeDeleted } = args as {
        tag?: string;
        includeDeleted?: boolean;
      };
      const slugs = [
        ...new Set(
          (tag ?? "")
            .split(",")
            .map((slug) => slug.trim())
            .filter((slug) => slug && slug !== "all"),
        ),
      ].slice(0, MAX_TAGS_IN_FILTER);
      if (slugs.length === 0) return undefined;

      const companyId = ctx.userContext?.companyId as
        | Id<"companies">
        | undefined;
      if (!companyId) {
        throw new Error("Usuario no tiene empresa asignada");
      }

      const tagDocs = (
        await Promise.all(
          slugs.map((slug) =>
            ctx.rawCtx.db
              .query("tags")
              .withIndex("by_company_and_slug", (q) =>
                q.eq("internalControl.companyId", companyId).eq("slug", slug),
              )
              .first(),
          ),
        )
      ).filter(
        (tagDoc): tagDoc is NonNullable<typeof tagDoc> =>
          !!tagDoc && tagDoc.internalControl?.state !== "deleted",
      );
      if (tagDocs.length === 0) return [];

      const links = (
        await Promise.all(
          tagDocs.map((tagDoc) =>
            ctx.rawCtx.db
              .query("entity_tags")
              .withIndex("by_company_and_tag", (q) =>
                q
                  .eq("internalControl.companyId", companyId)
                  .eq("tagId", tagDoc._id),
              )
              .collect(),
          ),
        )
      ).flat();

      const entityIds = [
        ...new Set(
          links
            .filter(
              (link) =>
                link.entityType === entityType &&
                link.internalControl?.state !== "deleted",
            )
            .map((link) => link.entityId),
        ),
      ];

      // Cuando una regla aplica, el pipeline no vuelve a acotar por sucursal:
      // lo hacemos aquí para que coincida con el listado sin filtros.
      const branchId =
        scope === "branch"
          ? (ctx.userContext?.branchId as Id<"branches"> | undefined)
          : undefined;

      const docs = await Promise.all(
        entityIds.map((id) => {
          const docId = ctx.rawCtx.db.normalizeId(entityType, id);
          return docId ? ctx.rawCtx.db.get(docId) : null;
        }),
      );

      return docs.filter((doc): doc is NonNullable<typeof doc> => {
        if (!doc) return false;
        const ic = (doc as { internalControl?: Doc<T>["internalControl"] })
          .internalControl;
        if (ic?.companyId !== companyId) return false;
        if (!includeDeleted && ic?.state === "deleted") return false;
        if (branchId && ic?.branchId !== branchId) return false;
        return true;
      }) as Doc<T>[];
    },
  };
}
